import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import {
  Video,
  Download,
  Play,
  Trash2,
  Calendar,
  User,
  Briefcase,
  Clock,
  Eye,
  RefreshCw,
  Search,
  Filter,
  FileVideo,
  HardDrive
} from "lucide-react";

const InterviewRecordings = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRecording, setSelectedRecording] = useState(null);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    fetchRecordings();
  }, []);

  const fetchRecordings = async () => {
    setLoading(true);
    try {
      const endpoint = user?.role === "recruiter" 
        ? `${API}/video-recordings/recruiter/candidates`
        : `${API}/video-recordings/my-recordings`;
        
      const response = await fetch(endpoint, {
        credentials: "include",
      });
      
      if (response.ok) {
        const data = await response.json();
        setRecordings(data);
      }
    } catch (error) {
      console.error("Failed to fetch recordings:", error);
      toast.error("Failed to load recordings");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (recording) => {
    try {
      const response = await fetch(`${API}${recording.download_url}`, {
        credentials: "include",
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `interview_${recording.recording_id}.webm`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success("Download started");
      } else {
        toast.error("Failed to download recording");
      }
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download recording");
    }
  };

  const handleDelete = async (recordingId) => {
    if (!confirm("Are you sure you want to delete this recording? This action cannot be undone.")) {
      return;
    }
    
    setDeleting(recordingId);
    try {
      const response = await fetch(`${API}/video-recordings/${recordingId}`, {
        method: "DELETE",
        credentials: "include",
      });
      
      if (response.ok) {
        toast.success("Recording deleted");
        fetchRecordings();
      } else {
        toast.error("Failed to delete recording");
      }
    } catch (error) {
      toast.error("Failed to delete recording");
    } finally {
      setDeleting(null);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "N/A";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  // Filter recordings based on search
  const filteredRecordings = user?.role === "recruiter"
    ? recordings.filter(candidate => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
          candidate.candidate_info?.name?.toLowerCase().includes(query) ||
          candidate.candidate_info?.email?.toLowerCase().includes(query)
        );
      })
    : recordings.filter(rec => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
          rec.job?.title?.toLowerCase().includes(query) ||
          rec.other_participant?.name?.toLowerCase().includes(query)
        );
      });

  return (
    <div className="min-h-screen bg-obsidian flex">
      <Sidebar />
      
      <main className="flex-1 lg:ml-64 p-4 sm:p-6 pt-20 lg:pt-6">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-8">
            <div>
              <h1 className="font-heading text-2xl sm:text-3xl font-bold mb-2">
                INTERVIEW <span className="text-crimson">RECORDINGS</span>
              </h1>
              <p className="text-gray-400 text-sm">
                {user?.role === "recruiter" 
                  ? "View and download candidate interview recordings"
                  : "Your recorded interview responses"
                }
              </p>
            </div>
            <Button onClick={fetchRecordings} variant="outline" className="border-[#333] text-white w-fit">
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
          </div>

          {/* Search */}
          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder={user?.role === "recruiter" ? "Search by candidate name..." : "Search recordings..."}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-obsidian-light border border-[#333] text-white placeholder-gray-500 focus:border-crimson focus:outline-none"
            />
          </div>

          {/* Loading State */}
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-2 border-crimson border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredRecordings.length === 0 ? (
            <div className="bg-obsidian-light border border-[#222] p-12 text-center">
              <FileVideo className="w-16 h-16 mx-auto mb-4 text-gray-600" />
              <h3 className="text-xl font-heading font-bold mb-2">No Recordings Found</h3>
              <p className="text-gray-400 text-sm">
                {searchQuery 
                  ? "No recordings match your search"
                  : user?.role === "recruiter"
                    ? "No candidate interview recordings available yet"
                    : "You haven't recorded any interviews yet"
                }
              </p>
            </div>
          ) : user?.role === "recruiter" ? (
            // Recruiter View - Grouped by Candidate
            <div className="space-y-6">
              {filteredRecordings.map((candidate, idx) => (
                <motion.div
                  key={candidate.candidate_id || idx}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className="bg-obsidian-light border border-[#222]"
                >
                  {/* Candidate Header */}
                  <div className="p-4 sm:p-6 border-b border-[#222]">
                    <div className="flex items-center gap-4">
                      {candidate.candidate_info?.picture ? (
                        <img 
                          src={candidate.candidate_info.picture} 
                          alt={candidate.candidate_info.name}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-crimson/20 flex items-center justify-center">
                          <User className="w-6 h-6 text-crimson" />
                        </div>
                      )}
                      <div>
                        <h3 className="font-heading text-lg font-bold">
                          {candidate.candidate_info?.name || "Unknown Candidate"}
                        </h3>
                        <p className="text-gray-400 text-sm">
                          {candidate.candidate_info?.email}
                        </p>
                      </div>
                      <div className="ml-auto text-right">
                        <span className="text-crimson font-bold text-lg">
                          {candidate.recordings?.length || 0}
                        </span>
                        <p className="text-gray-500 text-xs">Recordings</p>
                      </div>
                    </div>
                  </div>

                  {/* Recordings List */}
                  <div className="divide-y divide-[#222]">
                    {candidate.recordings?.map((recording) => (
                      <div 
                        key={recording.recording_id}
                        className="p-4 sm:p-6 hover:bg-white/5 transition-colors"
                      >
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                          <div className="flex items-start gap-4">
                            <div className="w-10 h-10 bg-crimson/10 flex items-center justify-center flex-shrink-0">
                              <Video className="w-5 h-5 text-crimson" />
                            </div>
                            <div>
                              <p className="font-medium text-white">
                                Recording {recording.recording_id.slice(-8)}
                              </p>
                              <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-gray-500">
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {formatDuration(recording.duration_seconds)}
                                </span>
                                <span className="flex items-center gap-1">
                                  <HardDrive className="w-3 h-3" />
                                  {formatFileSize(recording.file_size)}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  {formatDate(recording.created_at)}
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-2 ml-14 sm:ml-0">
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-[#333] text-white"
                              onClick={() => setSelectedRecording(recording)}
                              data-testid={`preview-${recording.recording_id}`}
                            >
                              <Play className="w-4 h-4 sm:mr-2" />
                              <span className="hidden sm:inline">Preview</span>
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleDownload(recording)}
                              className="bg-crimson hover:bg-crimson/90"
                              data-testid={`download-${recording.recording_id}`}
                            >
                              <Download className="w-4 h-4 sm:mr-2" />
                              <span className="hidden sm:inline">Download</span>
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                              onClick={() => handleDelete(recording.recording_id)}
                              disabled={deleting === recording.recording_id}
                              data-testid={`delete-${recording.recording_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            // Job Seeker View - Flat List
            <div className="space-y-4">
              {filteredRecordings.map((recording, idx) => (
                <motion.div
                  key={recording.recording_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className="bg-obsidian-light border border-[#222] p-4 sm:p-6"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-crimson/20 flex items-center justify-center flex-shrink-0">
                        <Video className="w-6 h-6 text-crimson" />
                      </div>
                      <div>
                        {recording.job && (
                          <p className="font-heading font-bold text-white">
                            {recording.job.title}
                          </p>
                        )}
                        <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-gray-400">
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {formatDuration(recording.duration_seconds)}
                          </span>
                          <span className="flex items-center gap-1">
                            <HardDrive className="w-4 h-4" />
                            {formatFileSize(recording.file_size)}
                          </span>
                        </div>
                        <p className="text-gray-500 text-xs mt-1">
                          Recorded {formatDate(recording.created_at)}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 ml-16 sm:ml-0">
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-[#333] text-white"
                        onClick={() => setSelectedRecording(recording)}
                      >
                        <Play className="w-4 h-4 mr-2" /> Preview
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleDownload(recording)}
                        className="bg-crimson hover:bg-crimson/90"
                      >
                        <Download className="w-4 h-4 mr-2" /> Download
                      </Button>
                      {recording.is_owner && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                          onClick={() => handleDelete(recording.recording_id)}
                          disabled={deleting === recording.recording_id}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Video Preview Modal */}
      {selectedRecording && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedRecording(null)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-obsidian-light border border-[#222] max-w-4xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-[#222] flex items-center justify-between">
              <h3 className="font-heading font-bold">Recording Preview</h3>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setSelectedRecording(null)}
                className="text-gray-400 hover:text-white"
              >
                ×
              </Button>
            </div>
            <div className="aspect-video bg-black">
              <video
                src={`${API}${selectedRecording.stream_url}`}
                controls
                autoPlay
                className="w-full h-full"
              />
            </div>
            <div className="p-4 flex items-center justify-between">
              <div className="text-sm text-gray-400">
                <span>{formatDuration(selectedRecording.duration_seconds)}</span>
                <span className="mx-2">•</span>
                <span>{formatFileSize(selectedRecording.file_size)}</span>
              </div>
              <Button
                onClick={() => handleDownload(selectedRecording)}
                className="bg-crimson hover:bg-crimson/90"
              >
                <Download className="w-4 h-4 mr-2" /> Download
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default InterviewRecordings;
